import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, ShieldCheck, TrendingUp, TrendingDown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const cap = (v) => (!v ? "" : v >= 1e12 ? `$${(v / 1e12).toFixed(1)}T` : v >= 1e9 ? `$${(v / 1e9).toFixed(0)}B` : `$${(v / 1e6).toFixed(0)}M`);

export const TopHalalStocks = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/halal-screener`, { params: { verdict: "Compliant", limit: 8 } })
      .then((r) => { setRows(r.data.items); setTotal(r.data.total); })
      .catch(() => {});
  }, []);

  if (!rows.length) return null;

  return (
    <section className="mx-auto max-w-7xl px-4 sm:px-6 py-16 sm:py-20" data-testid="top-halal-section">
      <div className="flex items-end justify-between gap-6 flex-wrap">
        <div className="max-w-2xl">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#EFF6D8] px-3 py-1 text-xs font-semibold text-[#3E2E65]">
            <ShieldCheck className="h-3.5 w-3.5" /> Free to browse
          </span>
          <h2 className="mt-4 text-3xl sm:text-4xl font-bold tracking-tight">Top halal stocks today</h2>
          <p className="mt-3 text-[#1A1330]/65">
            {total?.toLocaleString()} names are Shariah compliant on every AAOIFI threshold. Read the verdict
            and the numbers behind it before you open an account.
          </p>
        </div>
        <Button variant="outline" className="rounded-full px-6" onClick={() => navigate("/top-halal-stocks")}
          data-testid="top-halal-cta">
          Open the screener <ArrowRight className="h-4 w-4 ms-1.5" />
        </Button>
      </div>

      <div className="mt-9 grid sm:grid-cols-2 lg:grid-cols-4 gap-3.5">
        {rows.map((r) => {
          const up = (r.change_pct || 0) >= 0;
          return (
            <button key={r.symbol} onClick={() => navigate(`/halal-stocks/${encodeURIComponent(r.symbol)}`)}
              className="text-start rounded-2xl border border-black/5 bg-white p-5 transition-shadow hover:shadow-premium group"
              data-testid={`top-halal-${r.symbol}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-bold">{r.symbol}</div>
                  <div className="text-[11px] text-[#1A1330]/55 truncate">{r.name}</div>
                </div>
                <span className="rounded-full bg-emerald-50 border border-emerald-200 px-2 py-0.5 text-[9px] font-semibold text-emerald-700 whitespace-nowrap">
                  Shariah Compliant
                </span>
              </div>
              <div className="mt-4 flex items-end justify-between">
                <div>
                  <div className="numeric text-lg font-bold">
                    {r.currency === "USD" ? "$" : ""}{r.price?.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                    {r.currency !== "USD" && <span className="text-[10px] ms-1 text-[#1A1330]/50">{r.currency}</span>}
                  </div>
                  <div className="text-[10px] text-[#1A1330]/45">{r.market} · {cap(r.market_cap_usd)}</div>
                </div>
                <span className={`inline-flex items-center gap-0.5 numeric text-xs font-semibold ${up ? "text-emerald-600" : "text-rose-600"}`}>
                  {up ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
                  {up ? "+" : ""}{r.change_pct}%
                </span>
              </div>
              <div className="mt-3 flex items-center justify-between text-[10px] text-[#1A1330]/50">
                <span>Debt {r.debt_ratio ?? "—"}%</span>
                <span>Purify {r.purification_pct}%</span>
                <span className="text-[#3E2E65] font-semibold group-hover:underline">Read verdict</span>
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
};
